import { Suspense, useState } from 'react';
import { LazyLoader, Modal } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useNavigate } from 'react-router-dom';
import { GroupFormOther } from '../../../components/GroupForm/GroupFormOther';
import { updateClaims } from '../../../utils/authStore';
import { useQuery } from 'urql';
import { graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { GroupType } from 'client/gql/graphql';
import { CooperationGroupsTable } from 'components/Groups/tables/CooperationGroupsTable';

const SettingsCollaborationGroupsSubpageQueryDocument = graphql(`
  query SettingsCollaborationGroupsSubpageData(
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $profile: ID!
  ) {
    ...PermissionCheckerFields
    ...UseGroupsFields
  }
`);

export type GroupSettings = {
  cloudDirectory: boolean;
  asThreemaGroup: boolean;
  allowViewersCanAddMaterial: boolean;
  allowViewersCanCreateEvents: boolean;
  allowViewersCanStartMeeting: boolean;
};

export const SettingsCollaborationGroupsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const navigate = useNavigate();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const [formProfilesOpen, setFormProfilesOpen] = useState<boolean>(false);
  const [currentGroupUuid, setCurrentGroupUuid] = useState<string>('');

  const [{ data }] = useQuery({
    query: SettingsCollaborationGroupsSubpageQueryDocument,
    variables: {
      profile: profileUuid,
      groupWhere: {
        type: GroupType.Group,
        isCommunity: true,
        AND: [
          {
            organization_NOT: { uuid: organizationUuid },
          },
          {
            admins_SOME: { organization: { uuid: organizationUuid } },
          },
        ],
      },
      calendarEventWhere: {},
    },
  });

  const permissions = usePermissionChecker(data);

  const handleProfilesEdit = (uuid: string) => {
    setFormProfilesOpen(true);
    setCurrentGroupUuid(uuid);
  };

  function handleNavigate(uuid: string) {
    navigate('/groups/' + uuid);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canUpdateCollaborationGroups() || !pimAuthClaims.getOmniOrganization()}>
      <CooperationGroupsTable
        query={data}
        permissions={permissions}
        onNavigate={handleNavigate}
        onProfilesEdit={handleProfilesEdit}
      />

      <Modal
        isOpen={formProfilesOpen}
        onRequestClose={() => {
          setFormProfilesOpen(false);
          setCurrentGroupUuid('');
        }}
        title={t('groups.editGroup')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
          <GroupFormOther
            groupUuid={currentGroupUuid}
            onClose={async () => {
              await updateClaims(() => setClaimsTrigger(!claimsTrigger));
              setFormProfilesOpen(false);
              setCurrentGroupUuid('');
            }}
          />
        </Suspense>
      </Modal>
    </BpSubpage>
  );
};
